import { CountryCode, isSupportedCountry } from 'libphonenumber-js';

import { defaultLocale } from '@/config/locales';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';

/**
 * Resolves which country a phone-number field should default to: the country on the
 * user's account if we have one, otherwise the country of their IP, otherwise the
 * country of the default locale. `undefined` when none of them names a country
 * libphonenumber knows, which leaves the field with nothing selected rather than a
 * guess — see `PhoneNumberInput`.
 *
 * The *default* locale, not the active one, on purpose: it mirrors the country the API
 * falls back to when a profile has no address, so a legacy number stored without a `+`
 * is read the same on both sides. Following the site locale instead would reinterpret a
 * US number as British the moment a visitor on the UK domain touched the field.
 *
 * With no `userCountry` the IP lookup decides, and that's a client-side fetch — so
 * this returns the default first and re-resolves once the request lands. Consumers
 * must tolerate the value changing after mount.
 */
export const usePhoneDefaultCountry = (userCountry?: string): CountryCode | undefined => {
  const { data: geoData } = useGeoDataMyIp();

  for (const candidate of [userCountry, geoData?.country_code, defaultLocale.country]) {
    const code = candidate?.toUpperCase();

    if (code && isSupportedCountry(code)) {
      return code;
    }
  }

  return undefined;
};
